import { Component, ViewChild, ChangeDetectorRef, Injector, inject } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { GoogleMapComponent } from '@shared/components';
import { 
    FirestorePathsService, GeolocationService, MapHelperService, SettingsService, ShuttlesService
} from '@shared/services';
import { ViewBasePage } from '@shared/pages';
import type { TVector, TCoordinate, TAccountProfile, 
    TDealership, TSetting, TShuttle } from '@shared/models';

@Component({
    'selector': 'app-trackerLive',
    'templateUrl': './trackerLive.page.html',
    'styleUrls': ['./trackerLive.page.scss'],
    'standalone': true,
    'imports': [ CommonModule, FormsModule, IonicModule, GoogleMapComponent ]
})
export class TrackerLivePage extends ViewBasePage implements OnInit, OnDestroy {
    @ViewChild(GoogleMapComponent) public mapComponent!: GoogleMapComponent;
  
    public showBackButton: boolean;
    public isDebugMode = false;
    public vm: any = {};
    // public dealershipCoordinate: TCoordinate = { latitude: 0, longitude: 0 };  
    public dealershipCoordinate: { latitude: number; longitude: number; } = { latitude: 0, longitude: 0 };

    private changeDetectorRef = inject(ChangeDetectorRef);
    private accountProfile: TAccountProfile;
    private dealership: TDealership;
    private settings: TSetting;
    private shuttleMarkers: Map<string, string> = new Map(); // Maps shuttleId to marker id
    public shuttles: TShuttle[] = [];
    public trackerLegend = { 'visibility': false, 'showSimulationButton': false };
    private shuttlesSubscription: Subscription | null = null;
    private watchSubscription: Subscription | null = null;
    private centerIdx = -1;

    // private shuttleMovementService: ShuttleMovementService, private popoverController: PopoverController,
    constructor(private httpClient: HttpClient, private mapHelperService: MapHelperService,
                private firestorePathsService: FirestorePathsService, private shuttlesService: ShuttlesService,  
                private geolocationService: GeolocationService, private activatedRoute: ActivatedRoute,
                injector: Injector) {
        super(injector);
        // this.changeDetectorRef = inject(ChangeDetectorRef);
    }
    
    public override ionViewDidEnter() {
        super.setCurrentView(this.Constants.VIEW_TRACKER_LIVE);
    }

    public override async ngOnInit(): Promise<void> {
        super.ngOnInit();
        const vm = this.getViewModel();
        this.showBackButton = vm.sbb;

        try {
            this.accountProfile = this.sessionService.getAccountProfile();
            this.dealership = this.sessionService.getDealership();
            this.settings = await this.settingsService.get();

            // console.log('metadata.accountProfile = ', metadata.accountProfile);
            // console.log('metadata.settings = ', metadata.settings);
            
            const accountNumber = this.accountProfile.accountNumber;
            this.firestorePathsService.setAccountForAnonymousUsers(accountNumber);
            
            this.isDebugMode = this.accountProfile.debug;
            this.dealershipCoordinate = this.dealership.address.coordinate;
            this.trackerLegend.showSimulationButton = this.settings.misc.showSimulationButton;
            
            // this.vectors = await this.vectorsService.getAll();
            this.shuttles = await this.shuttlesService.getAll();
            this.shuttleMarkers = new Map();
    
            if (!this.accountProfile.featuresInfo.customer) {
                this.uxHelperService.showAlert('Sorry.', this.Constants.FEATURE_UPGRADE_TO_MANAGER_PACKAGE);
                return;
            }
    
            if (this.vb.disabled) {
                this.uxHelperService.showAlert('Error.', 'No Internet Connection.');
                return;
            }
    
            // const observable = this.vectorsService.getObservable();
            // this.vectorsSubscription = observable.subscribe((vectors: TVector[]) => {
            //     this.vectorsChange(vectors);
            // });
            const observable = this.shuttlesService.getObservable();
            this.shuttlesSubscription = observable.subscribe((shuttles: TShuttle[]) => {
                this.shuttlesChange(shuttles);
            });
            // this.registerSubscription(this.vectorsSubscription);
    
            // this.menuItems = ['Center Dealership', 'Center Next Shuttle', 'Show/Hide Info Panel'];
    
            // this.eventsService.subscribe(Constants.EVENT_DEBUG_MESSAGE, (msg) => this.onDebugMessage(msg));
            this.initMoreMenu();
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            this.uxHelperService.showAlert('Error', errorMessage);
        }
    }

    // private onDebugMessage(msg: string): void {
    //     this.vm.mapInfoStr = msg;
    // }

    public override ngOnDestroy(): void {
        this.watchSubscription?.unsubscribe();
        this.shuttlesSubscription?.unsubscribe();
        super.ngOnDestroy();
    }

    public initMoreMenu(): void {
        this.pushPopoverMenuItem('Center Dealership');
        this.pushPopoverMenuItem('Center Next Shuttle');
        this.pushPopoverMenuItem('Show/Hide Info Panel');
        this.pushPopoverMenuItem('Show Tour', 'show-live-tracker-tour');
        // if (this.accountProfile?.debug) {
        //     this.pushPopoverMenuItem('Get Current Location');
        // }
        
        this.moreMenuItemClick((menuItem) => {
            switch (menuItem.name) {
                case 'Center Dealership':
                    this.onCenterDealershipClick();
                    break;
    
                case 'Center Next Shuttle':
                    this.onCenterNextShuttle();
                    break;
    
                case 'Show/Hide Info Panel':
                    this.onShowHideInfoPanel();
                    break;
    
                case 'Get Current Location':
                    this.debugGetCurrentLocation();
                    break;
            }
        });        
    }

    // TAccountProfile
    
    public formatLocation(location: TCoordinate) {
        const latitude = this.utilsService.round(location.latitude, 7).toString();
        const longitude = this.utilsService.round(location.longitude, 7).toString();
        const str = latitude + ', ' + longitude;
        return str;
    }

    public formatCoordinateDelta(vector: TVector) {
        const deltaLatitude = vector.currentLocation.latitude - vector.previousLocation.latitude;
        const deltaLongitude = vector.currentLocation.longitude - vector.previousLocation.longitude;
        const latitude = this.utilsService.round(deltaLatitude, 5).toString();
        const longitude = this.utilsService.round(deltaLongitude, 5).toString();
        const str = latitude + ', ' + longitude;
        return str;
    }

    public formatDistance(distance: number) {
        const dist = this.utilsService.round(distance, 1).toString();
        return dist;
    }

    public formatSpeed(speed: number) {
        const dist = this.utilsService.round(speed, 0).toString();
        return dist;
    }

    // Show the shuttle markers in their updated positions on the map.
    public shuttlesChange(shuttles: TShuttle[]) {
        if (!shuttles) {
            return;
        }

        this.shuttles = this.shuttlesService.sort(shuttles);
        this.shuttles.forEach((shuttle: TShuttle) => {
            this.resetMarker(shuttle);
        });
    }

    /**
     * Handle map ready event - add dealership marker and initial shuttle markers
     */
    public async onMapReady(): Promise<void> {
        await this.uxHelperService.showSpinner('Loading map.');

        try {
            // Add dealership marker
            await this.createDealershipMarker();

            // Add initial shuttle markers
            await this.createShuttleMarkers();

            // Center map on dealership
            this.onCenterDealershipClick();
            await this.uxHelperService.hideSpinner();
        } catch (err) {
            await this.uxHelperService.hideSpinner();
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
        }
    }

    /**
     * Handle map error event
     */
    public onMapError(event: { message: string; error?: unknown }): void {
        // console.error('Map error:', event);
        this.uxHelperService.showAlert('Map Error', event.message);
    }

    /**
     * Create dealership marker with star icon
     */
    private async createDealershipMarker(): Promise<void> {
        const dealershipName = this.dealership.dealershipName;
        const iconUrl = this.mapHelperService.getMarkerIcon('Dealership');

        this.mapComponent.addDealershipMarker(
            this.dealershipCoordinate,
            dealershipName,
            iconUrl
        );
    }


    /**
     * Create markers for each of the shuttles
     */
    private async createShuttleMarkers(): Promise<void> {
        for (const shuttle of this.shuttles) {
            const vector = shuttle.vector;
            const shuttleColor = shuttle.color;
            const shuttleIcon = this.mapHelperService.getMarkerIcon(shuttleColor);
            const shuttleName = shuttle.shuttleName;
            const shuttleDesc = shuttle.description;
            const driverName = shuttle.driverName;

            const coordinate = {
                latitude: vector.currentLocation.latitude,
                longitude: vector.currentLocation.longitude
            };

            // Add shuttle marker with initial heading of 0 (will be updated by resetMarker)
            this.mapComponent.addShuttleMarker(
                shuttleName,
                coordinate,
                shuttleIcon,
                shuttleColor,
                0 // Initial heading
            );

            // Track this marker
            this.shuttleMarkers.set(shuttleName, shuttleName);

            // Set initial position and visibility
            this.resetMarker(shuttle);
        }
    }

    public getDriverFullName(vector) {
        // let driverName = vector?.driver?.fullName;
        let driverName = 'No Driver';
        if (vector && vector.driver && vector.driver.fullName) {
            driverName = vector.driver.fullName;
        }
        return driverName;
    }

    // The visibility of this shuttle marker will change over time.
    // If the shuttle is at the dealership, then the shuttle marker should
    // not be shown on the map, because if it is shown then either it will 
    // land on top of the dealership icon or be hidden by the dealership icon.
    // In either case the shuttle icon marker should not be shown if the 
    // shuttle is at the dealership.  
    private computeMarkerVisibility(vector: TVector): boolean {
        vector.distanceFromDealership = vector.distanceFromDealership || { 'miles': 0, 'kilometers': 0 };
        const visible = (vector.distanceFromDealership.miles > 0.10);
        return visible;
    }

    /**
     * Update shuttle marker position with smooth CSS transition
     * Hides marker if shuttle is at dealership (< 0.10 miles)
     */
    private resetMarker(shuttle: TShuttle): void {
        const shuttleName = shuttle.shuttleName;
        const coordinate = shuttle.vector.currentLocation;

        if (!this.shuttleMarkers.has(shuttleName)) {
            return;
        }

        // Determine if the marker should be shown
        const visible = this.computeMarkerVisibility(shuttle.vector);

        if (visible) {
            // Calculate heading from previous to current position
            const heading = this.calculateHeading(
                shuttle.vector.previousLocation,
                shuttle.vector.currentLocation
            );

            // Update shuttle position with smooth CSS transition (0.5s linear)
            this.mapComponent.updateShuttle(
                shuttleName,
                coordinate.latitude,
                coordinate.longitude,
                heading
            );
        } else {
            // Hide shuttle by moving to dealership position
            this.mapComponent.updateShuttle(
                shuttleName,
                this.dealershipCoordinate.latitude,
                this.dealershipCoordinate.longitude,
                0 // Heading doesn't matter when hidden
            );
        }
    }

    /**
     * Calculate heading (bearing) between two coordinates
     * Returns degrees (0-360, where 0=North, 90=East, 180=South, 270=West)
     */
    private calculateHeading(from: TCoordinate, to: TCoordinate): number {
        if (!from || !to) {
            return 0;
        }

        const lat1 = from.latitude * Math.PI / 180;
        const lat2 = to.latitude * Math.PI / 180;
        const dLng = (to.longitude - from.longitude) * Math.PI / 180;

        const y = Math.sin(dLng) * Math.cos(lat2);
        const x = Math.cos(lat1) * Math.sin(lat2) -
                  Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

        const heading = Math.atan2(y, x) * 180 / Math.PI;
        
        // Normalize to 0-360
        return (heading + 360) % 360;
    }

    public getNearbyLocation(vector) {
        let nearBy = '';
        if (vector.distanceFromDealership.miles < 0.10) {
            nearBy = 'Near: At Dealership';
        }
        return nearBy;
    }

    // public async onMoreClick(event) {
    //     const options: PopoverOptions = {
    //         'component': PopoverMenu,
    //         'componentProps': { 'menuItems': this.menuItems },
    //         'event': event,
    //         'showBackdrop': true,
    //         'cssClass': 'myNavigationMoreMenuClass'
    //     };

    //     const popover = await this.popoverController.create(options);
    //     popover.onDidDismiss()
    //     .then((response: any) => {
    //         if (response.data) {
    //             switch (response.data.selectedItem) {
    //                 case 'Center Dealership':
    //                     this.onCenterDealershipClick();
    //                     break;

    //                 case 'Center Next Shuttle':
    //                     this.onCenterNextShuttle();
    //                     break;

    //                 case 'Show/Hide Info Panel':
    //                     this.onShowHideInfoPanel();
    //                     break;

    //                 case 'Debug Get Current Location':
    //                     this.debugGetCurrentLocation();
    //                     break;
    //             }
    //         }
    //     });
    //     popover.present();
    // }

    /**
     * Recenter the map so the dealership is in the center of the view
     */
    private onCenterDealershipClick(): void {
        this.mapComponent.centerMap(this.dealershipCoordinate);
    }

    /**
     * Center map on next traveling shuttle (cycles through shuttles)
     */
    private async onCenterNextShuttle(): Promise<void> {
        const shuttlesOutTraveling = this.shuttles.filter((shuttle) => shuttle.status === 'Traveling');

        const numShuttles = shuttlesOutTraveling.length;
        if (numShuttles === 0) {
            await this.uxHelperService.showAlert('Information', 'All shuttles are at the dealership.');
            this.onCenterDealershipClick();
            return;
        }

        this.centerIdx = (this.centerIdx >= numShuttles - 1) ? 0 : ++this.centerIdx; 
        const shuttle = shuttlesOutTraveling[this.centerIdx];
        const vector = shuttle.vector;
        
        this.mapComponent.centerMap({
            latitude: vector.currentLocation.latitude,
            longitude: vector.currentLocation.longitude
        });
    }

    private async onShowHideInfoPanel() {
        this.trackerLegend.visibility = !this.trackerLegend.visibility;
    }

    private async debugGetCurrentLocation() {
        // const watchObservable = this.geolocationService.getPositionObservable();
        let counter = 0;

        const watchObservable = this.geolocationService.getPositionObservable(async (data: any) => {
        // this.watchSubscription = watchObservable.subscribe((data: any) => {
            ++counter;
            const latitude = data.coords.latitude.toString();
            const longitude = data.coords.longitude.toString();
            const timestamp = data.timestamp;
            // const time =  mom-ent(timestamp).format('h:mm:ss a');
            const msg = `${counter}: (${latitude}, ${longitude})`;  //  at ${time}
            this.vm.mapInfoStr = msg;
            // shuttle1Vector.currentLocation = {'latitude': latitude, 'longitude': longitude};

            if (counter > 50) {
                this.watchSubscription.unsubscribe();
            }
        });
    }
}

/*
export class MyMap {
  @ViewChild('map')
  mapRef: ElementRef<HTMLElement>;
  newMap: GoogleMap;

  async createMap() {
    this.newMap = await GoogleMap.create({
      id: 'my-cool-map',
      element: this.mapRef.nativeElement,
      apiKey: environment.apiKey,
      config: {
        center: {
          lat: 33.6,
          lng: -117.9,
        },
        zoom: 8,
      },
    });
  }
}
*/
// capacitor-google-map {
//   display: inline-block;
//   width: 275px;
//   height: 400px;
// }


/*
        const options = {
            'center': this.dealershipLatLng,    // Show the map with the dealership at the center
            'zoom': 15,
            'mapTypeControl': false,            // Displays Map or Satellite view
            'mapTypeControlOptions': {
                'style': google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                'position': google.maps.ControlPosition.TOP_CENTER
            },
            'zoomControl': true,
            'zoomControlOptions': { 'position': google.maps.ControlPosition.RIGHT_TOP },
            'scaleControl': true,
            'streetViewControl': false,
            'streetViewControlOptions': { 'position': google.maps.ControlPosition.LEFT_TOP },
            'fullscreenControl': false
        };
        this.map = new google.maps.Map(this.mapRef.nativeElement, options);
        this.mapInfo = {};

        // When zoom level changes rebuild the map info string.
        this.buildZoom();
        this.map.addListener('zoom_changed', () => {
            this.buildZoom();
        });

        // When map center changes rebuild the map info string.
        this.buildMapCenter();
        this.map.addListener('center_changed', () => {
            this.buildMapCenter();
        });
*/
